import axios from 'axios';
import { API_BASE_URL } from '../config/api-config';

// Create a dedicated axios instance
export const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true
});

// Request interceptor: attach impersonation token as Bearer if present
api.interceptors.request.use((config) => {
    const impersonationToken = sessionStorage.getItem('impersonationToken');
    if (impersonationToken) {
        config.headers.Authorization = `Bearer ${impersonationToken}`;
    }
    return config;
});

// Flag to track if a refresh is in progress
let isRefreshing = false;
// Queue to store subscribers waiting for the token to refresh
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
    refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
    refreshSubscribers.map((cb) => cb(token));
    refreshSubscribers = [];
};

// Response interceptor for automatic token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { config, response } = error;
        const originalRequest = config;

        // Skip refresh logic for auth-related endpoints and non-401 errors
        const isAuthRequest = originalRequest.url?.includes('/user/login') ||
            originalRequest.url?.includes('/user/telegram-login') ||
            originalRequest.url?.includes('/user/refresh') ||
            originalRequest.url?.includes('/user/logout') ||
            originalRequest.url?.includes('/user/register') ||
            originalRequest.url?.includes('/impersonate/activate') ||
            /(^|\/)tg\//.test(originalRequest.url ?? '');

        // Don't attempt refresh for impersonation tokens — they're short-lived and non-refreshable
        const isImpersonating = typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('impersonationToken');

        if (response?.status === 401 && !originalRequest._retry && !isAuthRequest && !isImpersonating) {
            if (isRefreshing) {
                // If refresh is already in progress, wait for it to complete
                return new Promise((resolve) => {
                    subscribeTokenRefresh(() => {
                        // Retry original request
                        resolve(api(originalRequest));
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                console.log('🔄 Automatic token refresh triggered...');
                // Trigger server-side token exchange (sets HttpOnly cookies)
                await api.post('/user/refresh', {});

                // Give the browser a short moment to apply Set-Cookie (some runtimes need a tiny delay)
                await new Promise(resolve => setTimeout(resolve, 100));

                // Verify that the refresh actually produced a valid session by performing
                // a lightweight, credentialed fetch that bypasses axios interceptors.
                // If this fails (still 401), we must treat refresh as failed.
                try {
                    const profileUrl = (API_BASE_URL || '').replace(/\/$/, '') + '/user/profile';
                    const resp = await fetch(profileUrl, { method: 'GET', credentials: 'include' });
                    if (!resp.ok) throw new Error(`Profile verification failed: ${resp.status}`);
                } catch (verifyErr) {
                    throw verifyErr;
                }

                console.log('✅ Token refreshed successfully');

                isRefreshing = false;
                onTokenRefreshed('refreshed'); // Token is in HttpOnly cookie, so string doesn't matter much here

                return api(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                refreshSubscribers = [];
                console.error('❌ Token refresh failed:', refreshError);

                // Notify application that session is invalid
                window.dispatchEvent(new CustomEvent('auth:unauthorized'));

                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// Configure global defaults as fallback
axios.defaults.withCredentials = true;
axios.defaults.baseURL = API_BASE_URL;
